/**
 * Abfeco Wastafel Configurator — v5
 * File: assets/configurator.js
 *
 * Step order:
 * 1. Type (alleen / met blad / met meubel / monoblok)
 * 2. Basin (varies by type)
 * 3. Afmetingen (varies by type)
 * 4. Materiaal & afwerking
 * 5. Meubel details (only when "met meubel")
 * 6. Extra's
 * 7. Gegevens
 * 8. Overzicht
 *
 * Monoblok variations are handled inline based on selected type.
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────────
  // HELPERS for dynamic choices
  // ─────────────────────────────────────────────
  function isMonoblok(state) {
    return state.selections.type === 'Monoblok wastafel';
  }

  function isHetMeubel(state) {
    return state.selections.type === 'Wastafel met meubel';
  }

  function isMetBladOrMeubel(state) {
    return state.selections.type === 'Wastafel met blad' || state.selections.type === 'Wastafel met meubel';
  }

  function getLadenChoices(breedte) {
    if (!breedte || breedte < 120) return ['1'];
    if (breedte < 180) return ['1', '2'];
    return ['1', '2', '3'];
  }

  function getBreedteChoices(aantalBakken) {
    // More bakken = larger minimum breedte
    if (aantalBakken >= 3) return ['160', '180', '200', '220', ];
    if (aantalBakken === 2) return ['110', '120', '140', '160', '180', '200','220'];
    return ['60', '80', '100', '120', '140', '160', '200','220'];
  }

  function getMinBreedte(aantalBakken) {
    if (aantalBakken >= 3) return 150;
    if (aantalBakken === 2) return 100;
    return 30;
  }

  // ─────────────────────────────────────────────
  // STEP DEFINITIONS
  // ─────────────────────────────────────────────
  const STEPS = [
    // STEP 1 — TYPE
    {
      id: 'type',
      shortLabel: 'Type',
      title: 'Wat wil je samenstellen?',
      desc: 'Kies wat voor wastafel je wilt. Dit bepaalt welke opties je verder ziet.',
      questions: () => [
        {
          id: 'type',
          label: 'Type',
          required: true,
          choices: ['Alleen een wastafel', 'Wastafel met blad', 'Wastafel met meubel', 'Monoblok wastafel']
        }
      ]
    },

    // STEP 2 — BASIN / MONOBLOK SUBTYPE
    {
      id: 'wasbak',
      shortLabel: 'Basin',
      title: 'Wat voor basin?',
      desc: 'Kies het type basin en aantal kraangaten.',
      questions: (state) => {
        // MONOBLOK path
        if (isMonoblok(state)) {
          const q = [
            {
              id: 'monoblokType',
              label: 'Type monoblok',
              required: true,
              choices: ['Enkele monoblok wastafel', 'Dubbele monoblok wastafel', 'Ergonomische wastafel']
            }
          ];
          if (state.selections.monoblokType === 'Dubbele monoblok wastafel') {
            q.push({
              id: 'kraangat',
              label: 'Kraangat per bak',
              required: true,
              choices: ['Ja, bovenop', 'Nee, kraan in muur', 'Weet ik nog niet']
            });
          } else if (state.selections.monoblokType) {
            q.push({
              id: 'kraangat',
              label: 'Kraangat',
              required: true,
              choices: ['Ja, bovenop', 'Nee, kraan in muur', 'Weet ik nog niet']
            });
          }
          return q;
        }

        // STANDARD path (alleen / met blad / met meubel)
        const q = [
          {
            id: 'basinType',
            label: 'Type basin',
            required: true,
            choices: ['Wastafel (ingebouwd)', 'Waskom (los, opbouw)']
          },
          {
            id: 'aantalWasbakken',
            label: 'Aantal wasbakken',
            required: true,
            choices: ['1', '2', '3']
          },
          {
            id: 'aantalKraangaten',
            label: 'Aantal kraangaten',
            required: true,
            choices: ['0', '1', '2', '3']
          }
        ];
        // if (state.selections.basinType === 'Waskom (los, opbouw)') {
        //   q.push({
        //     id: 'wasbakVorm',
        //     label: 'Vorm wasbak',
        //     required: true,
        //     choices: ['Rechthoek', 'Ovaal', 'Rond']
        //   });
        // }
        return q;
      }
    },

    // STEP 3 — AFMETINGEN
    {
      id: 'afmetingen',
      shortLabel: 'Afmetingen',
      title: 'Afmetingen',
      desc: 'Kies de gewenste afmetingen. Kies maatwerk als je een specifieke maat wilt.',
      questions: (state) => {
        // MONOBLOK path
        if (isMonoblok(state)) {
          const sub = state.selections.monoblokType;
          if (sub === 'Enkele monoblok wastafel') {
            return [
              {
                id: 'maat',
                label: 'Maat (B × D × H)',
                required: true,
                choices: ['45 × 45 × 85 cm', '60 × 60 × 85 cm'],
                allowCustom: true, customMin: 30, customMax: 120, customUnit: 'cm',
                customLabel: 'Voer breedte in cm in (diepte en hoogte volgen later op maat)'
              }
            ];
          }
          if (sub === 'Dubbele monoblok wastafel') {
            return [
              {
                id: 'maat',
                label: 'Maat per bak (B × D × H)',
                required: true,
                choices: ['45 × 45 × 85 cm', '60 × 60 × 85 cm'],
                allowCustom: true, customMin: 30, customMax: 120, customUnit: 'cm'
              },
              {
                id: 'plankLengte',
                label: 'Lengte van de plank',
                required: true,
                unit: 'cm',
                choices: ['180', '200', '220', '240'],
                allowCustom: true, customMin: 100, customMax: 300, customUnit: 'cm'
              }
            ];
          }
          if (sub === 'Ergonomische wastafel') {
            return [
              {
                id: 'maat',
                label: 'Maat (B × D × H)',
                required: true,
                choices: ['50 × 46 × 92 cm (standaard)'],
                allowCustom: true, customMin: 30, customMax: 120, customUnit: 'cm',
                customLabel: 'Voer breedte in cm in (diepte en hoogte op maat)'
              }
            ];
          }
          return [];
        }

        // STANDARD path
        const aantalBakken = parseInt(state.selections.aantalWasbakken) || 1;
        const q = [
          {
            id: 'breedte',
            label: 'Breedte wastafel',
            required: true,
            unit: 'cm',
            choices: getBreedteChoices(aantalBakken),
            allowCustom: true,
            customMin: getMinBreedte(aantalBakken),
            customMax: 400,
            customUnit: 'cm'
          },
          {
            id: 'diepte',
            label: 'Diepte wastafel',
            required: true,
            unit: 'cm',
            choices: ['45', ],
            allowCustom: true, customMin: 30, customMax: 80, customUnit: 'cm'
          },
          {
            id: 'dikteWastafel',
            label: 'Dikte wastafel',
            required: true,
            unit: 'cm',
            choices: [  '14'],
            allowCustom: true, customMin: 4, customMax: 20, customUnit: 'cm'
          }
        ];
        if (isMetBladOrMeubel(state)) {
          q.push({
            id: 'diepteBlad',
            label: 'Diepte blad / meubel',
            required: true,
            unit: 'cm',
            choices: ['45', ],
            allowCustom: true, customMin: 30, customMax: 80, customUnit: 'cm'
          });
          if(

state.selections.type == 'Wastafel met blad'

          )
            
           {q.push({
            id: 'dikteBlad',
            label: 'Dikte blad',
            required: true,
            unit: 'cm',
            choices: ['10'],
            allowCustom: true, customMin: 8, customMax: 20, customUnit: 'cm'
          });} 
            
          // q.push({
          //   id: 'hoogte',
          //   label: 'Hoogte vanaf vloer',
          //   required: true,
          //   unit: 'cm',
          //   choices: ['80', '85', '90', '95'],
          //   allowCustom: true, customMin: 60, customMax: 120, customUnit: 'cm'
          // });
        }
        return q;
      }
    },

    // STEP 4 — MATERIAAL
    {
      id: 'materiaal',
      shortLabel: 'Materiaal',
      title: 'Materiaal & afwerking',
      desc: 'Het hoofdmateriaal is keramiek. Kies de gewenste kleur en oppervlak.',
      questions: (state) => {
        const q = [
          {
            id: 'materiaalWastafel',
            label: 'Materiaal wastafel',
            required: true,
            choices: ['Keramiek']
          },
          {
            id: 'kleurWastafel',
            label: 'Kleur wastafel',
            required: true,
            choices: ['Wit mat', 'Mat zwart', 'Beton grijs', 'Antraciet', 'Marmer','Travertijn']
          },
          {
            id: 'oppervlakWastafel',
            label: 'Oppervlak wastafel',
            required: true,
            choices: ['Hoogglans', 'Mat', 'Halfmat']
          }
        ];
        // Blad/meubel material questions only for standard "met blad / met meubel"
        if (isMetBladOrMeubel(state)) {
          // q.push({
          //   id: 'materiaalOnder',
          //   label: 'Materiaal blad / meubel',
          //   required: true,
          //   choices: ['Keramiek']
          // });
          q.push({
            id: 'kleurOnder',
            label: 'Kleur blad / meubel',
            required: true,
            choices: ['Wit mat', 'Mat zwart', 'Eiken', 'Walnoot', 'Beton grijs','Travertijn']
          });
        }
        // For dubbele monoblok, ask about the plank material
        if (state.selections.monoblokType === 'Dubbele monoblok wastafel') {
          q.push({
            id: 'plankMateriaal',
            label: 'Materiaal plank',
            required: true,
            choices: ['Eiken', 'Walnoot', 'Keramiek', 'Marmer']
          });
        }
        return q;
      }
    },

    // STEP 5 — MEUBEL DETAILS (conditional, only for "met meubel")
    {
      id: 'meubel',
      shortLabel: 'Meubel',
      title: 'Meubel details',
      desc: 'Het aantal laden wordt aangepast aan de gekozen breedte.',
      condition: (state) => isHetMeubel(state),
      questions: (state) => {
        const breedte = parseInt(state.selections.breedte) || 0;
        return [
          {
            id: 'aantalLaden',
            label: 'Aantal laden',
            required: true,
            choices: getLadenChoices(breedte)
          },
          {
            id: 'greep',
            label: 'Greep type',
            required: true,
            choices: ['Geen greep', 'Handgreep', 'Push-to-open']
          }
        ];
      }
    },

    // STEP 6 — EXTRAS
    {
      id: 'extras',
      shortLabel: "Extra's",
      title: "Extra's",
      desc: 'Optioneel — kies wat van toepassing is voor jouw situatie.',
      questions: () => [
        // {
        //   id: 'sifon',
        //   label: 'Sifon kleur',
        //   required: false,
        //   choices: ['Chroom', 'Mat zwart', 'Geborsteld RVS']
        // },
        {
          id: 'ledSpiegel',
          label: 'LED spiegel erbij?',
          required: false,
          choices: ['Nee', 'Ja']
        },
        {
          id: 'montage',
          label: 'Montage door Abfeco',
          required: false,
          choices: ['Ja', 'Nee']
        }
      ]
    },

    // STEP 7 — CONTACT
    {
      id: 'gegevens',
      shortLabel: 'Gegevens',
      title: 'Jouw gegevens',
      desc: 'Zodat we contact kunnen opnemen met een offerte op maat.',
      isContactForm: true
    },

    // STEP 8 — SUMMARY
    {
      id: 'overzicht',
      shortLabel: 'Overzicht',
      title: 'Overzicht',
      desc: 'Controleer je keuzes en verstuur de aanvraag.',
      isSummary: true
    }
  ];

  // ─────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────
  const state = {
    view: 'gallery',
    currentStep: 1,
    selections: {},
    contact: {},
    lightboxIndex: 0
  };

  // ─────────────────────────────────────────────
  // DOM HELPERS
  // ─────────────────────────────────────────────
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  // ─────────────────────────────────────────────
  // VIEW SWITCHING
  // ─────────────────────────────────────────────
  function showView(name) {
    state.view = name;
    const gallery = $('#view-gallery');
    const configurator = $('#view-configurator');
    if (name === 'gallery') {
      gallery.classList.remove('afc-view--hidden');
      configurator.classList.add('afc-view--hidden');
    } else {
      gallery.classList.add('afc-view--hidden');
      configurator.classList.remove('afc-view--hidden');
      goToStep(1);
    }
    const top = $('#abfeco-configurator .afc-main');
    if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ─────────────────────────────────────────────
  // STEP NAVIGATION
  // ─────────────────────────────────────────────
  function getActiveSteps() {
    return STEPS.filter(s => !s.condition || s.condition(state));
  }

  function goToStep(n) {
    const active = getActiveSteps();
    if (n < 1) n = 1;
    if (n > active.length) n = active.length;

    if (n > state.currentStep && !validateCurrentStep()) return;

    state.currentStep = n;
    renderStepBar();
    renderCurrentStep();

    const top = $('.afc-view--configurator');
    if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function renderStepBar() {
    const active = getActiveSteps();
    const bar = $('.afc-steps');
    if (!bar) return;

    bar.innerHTML = '';
    active.forEach((step, idx) => {
      const num = idx + 1;
      const div = document.createElement('div');
      div.className = 'afc-step';
      if (num === state.currentStep) div.classList.add('is-active');
      if (num < state.currentStep) div.classList.add('is-done');
      div.dataset.step = num;
      div.innerHTML = `
        <div class="afc-step__num">${num}</div>
        <span class="afc-step__label">${step.shortLabel}</span>
      `;
      bar.appendChild(div);

      if (idx < active.length - 1) {
        const line = document.createElement('div');
        line.className = 'afc-step__line';
        bar.appendChild(line);
      }
    });
  }

  // ─────────────────────────────────────────────
  // RENDER CURRENT STEP
  // ─────────────────────────────────────────────
  function renderCurrentStep() {
    const active = getActiveSteps();
    const step = active[state.currentStep - 1];
    if (!step) return;

    const panels = $('#afc-panels');
    if (!panels) return;

    if (step.isContactForm) return renderContactStep(panels, step, active);
    if (step.isSummary) return renderSummaryStep(panels, step, active);

    renderQuestionsStep(panels, step, active);
  }

  function renderQuestionsStep(container, step, active) {
    const questions = step.questions(state);
    const stepNum = state.currentStep;
    const total = active.length;

    container.innerHTML = `
      <div class="afc-panel">
        <p class="afc-eyebrow">Stap ${stepNum} van ${total}</p>
        <h2 class="afc-panel__title">${step.title}</h2>
        <p class="afc-panel__desc">${step.desc}</p>
        <div class="afc-questions" id="afc-questions"></div>
        <div class="afc-nav">
          ${stepNum > 1 ? '<button class="afc-btn afc-btn--ghost afc-btn--back" type="button">← Vorige</button>' : '<span></span>'}
          <button class="afc-btn afc-btn--primary afc-btn--next" type="button">Volgende stap →</button>
        </div>
      </div>
    `;

    const qContainer = $('#afc-questions', container);
    questions.forEach(q => renderQuestion(qContainer, q));

    const backBtn = $('.afc-btn--back', container);
    if (backBtn) backBtn.addEventListener('click', () => goToStep(state.currentStep - 1));
    const nextBtn = $('.afc-btn--next', container);
    nextBtn.addEventListener('click', () => goToStep(state.currentStep + 1));
  }

  function renderQuestion(container, q) {
    const group = document.createElement('div');
    group.className = 'afc-option-group';

    const currentVal = state.selections[q.id];
    const isCustomSelected = q.allowCustom && currentVal && String(currentVal).includes('(maatwerk)');

    group.innerHTML = `
      <div class="afc-option-group__label">
        ${q.label}
        ${q.required ? '<span class="afc-required">*</span>' : '<span class="afc-optional">(optioneel)</span>'}
      </div>
      <div class="afc-pills" data-option-id="${q.id}">
        ${q.choices.map(c => {
          const display = q.unit ? `${c} ${q.unit}` : c;
          const isSel = currentVal === display || currentVal === c;
          return `<button class="afc-pill${isSel ? ' is-selected' : ''}" data-value="${display}" data-option="${q.id}" type="button">${display}</button>`;
        }).join('')}
        ${q.allowCustom ? `<button class="afc-pill afc-pill--custom${isCustomSelected ? ' is-selected' : ''}" data-option="${q.id}" data-custom="1" type="button">Maatwerk</button>` : ''}
      </div>
      ${q.allowCustom ? `
        <div class="afc-custom-input ${isCustomSelected ? '' : 'afc-custom-input--hidden'}" data-option="${q.id}">
          <label class="afc-custom-input__label">${q.customLabel || `Voer maatwerk in (${q.customMin}-${q.customMax} ${q.customUnit})`}</label>
          <div class="afc-custom-input__wrap">
            <input type="number" class="afc-custom-input__field" min="${q.customMin}" max="${q.customMax}" placeholder="bijv. ${Math.round((q.customMin + q.customMax) / 2)}" data-option="${q.id}" data-unit="${q.customUnit}" value="${isCustomSelected ? String(currentVal).replace(/[^\d.]/g, '') : ''}">
            <span class="afc-custom-input__unit">${q.customUnit}</span>
          </div>
        </div>
      ` : ''}
    `;
    container.appendChild(group);

    $$('.afc-pill', group).forEach(pill => {
      pill.addEventListener('click', () => handlePillClick(pill, q));
    });

    const customInput = $('.afc-custom-input__field', group);
    if (customInput) {
      customInput.addEventListener('input', () => handleCustomInput(customInput, q));
    }
  }

  function handlePillClick(pill, q) {
    const optionId = pill.dataset.option;
    const isCustom = pill.dataset.custom === '1';
    const group = pill.closest('.afc-option-group');

    $$(`.afc-pill[data-option="${optionId}"]`, group).forEach(p => p.classList.remove('is-selected'));
    pill.classList.add('is-selected');

    const customWrap = $('.afc-custom-input', group);

    if (isCustom) {
      if (customWrap) customWrap.classList.remove('afc-custom-input--hidden');
      const input = $('.afc-custom-input__field', group);
      if (input) {
        input.focus();
        if (input.value) {
          state.selections[optionId] = `${input.value} ${q.customUnit} (maatwerk)`;
        } else {
          delete state.selections[optionId];
        }
      }
    } else {
      if (customWrap) customWrap.classList.add('afc-custom-input--hidden');
      state.selections[optionId] = pill.dataset.value;
    }

    // Re-render current step if a selection changed that affects other questions
    if (optionId === 'basinType' || optionId === 'monoblokType') {
      renderCurrentStep();
    }

    // If aantal wasbakken changes, clear any breedte that's now invalid
    if (optionId === 'aantalWasbakken') {
      const newCount = parseInt(pill.dataset.value) || 1;
      const minB = getMinBreedte(newCount);
      const currentBreedte = parseInt(state.selections.breedte) || 0;
      if (currentBreedte && currentBreedte < minB) {
        delete state.selections.breedte;
      }
    }

    updateSidebar();
  }

  function handleCustomInput(input, q) {
    const optionId = input.dataset.option;
    const val = input.value;
    if (val && parseFloat(val) >= q.customMin && parseFloat(val) <= q.customMax) {
      state.selections[optionId] = `${val} ${q.customUnit} (maatwerk)`;
    } else {
      delete state.selections[optionId];
    }
    updateSidebar();
  }

  // ─────────────────────────────────────────────
  // CONTACT STEP
  // ─────────────────────────────────────────────
  function renderContactStep(container, step, active) {
    const stepNum = state.currentStep;
    const total = active.length;
    container.innerHTML = `
      <div class="afc-panel">
        <p class="afc-eyebrow">Stap ${stepNum} van ${total}</p>
        <h2 class="afc-panel__title">${step.title}</h2>
        <p class="afc-panel__desc">${step.desc}</p>
        <div class="afc-fields">
          <div class="afc-field">
            <label class="afc-field__label" for="c-naam">Naam *</label>
            <input class="afc-field__input" type="text" id="c-naam" placeholder="Jouw naam" value="${state.contact.name || ''}">
          </div>
          <div class="afc-field">
            <label class="afc-field__label" for="c-email">E-mail *</label>
            <input class="afc-field__input" type="email" id="c-email" placeholder="jouw@email.nl" value="${state.contact.email || ''}">
          </div>
          <div class="afc-field">
            <label class="afc-field__label" for="c-tel">Telefoon</label>
            <input class="afc-field__input" type="tel" id="c-tel" placeholder="+31 6 12345678" value="${state.contact.phone || ''}">
          </div>
          <div class="afc-field">
            <label class="afc-field__label" for="c-pc">Postcode</label>
            <input class="afc-field__input" type="text" id="c-pc" placeholder="3134 AB" value="${state.contact.postcode || ''}">
          </div>
          <div class="afc-field afc-field--full">
            <label class="afc-field__label" for="c-msg">Aanvullende opmerkingen</label>
            <textarea class="afc-field__input afc-field__textarea" id="c-msg" rows="4" placeholder="Bijv. gewenste leveringstijd, specifieke wensen...">${state.contact.message || ''}</textarea>
          </div>
        </div>
        <div class="afc-nav">
          <button class="afc-btn afc-btn--ghost afc-btn--back" type="button">← Vorige</button>
          <button class="afc-btn afc-btn--primary afc-btn--next" type="button">Naar overzicht →</button>
        </div>
      </div>
    `;

    $('.afc-btn--back', container).addEventListener('click', () => goToStep(state.currentStep - 1));
    $('.afc-btn--next', container).addEventListener('click', () => {
      if (!validateContact(container)) return;
      saveContact(container);
      goToStep(state.currentStep + 1);
    });

    $$('.afc-field__input', container).forEach(i => {
      i.addEventListener('input', () => i.closest('.afc-field').classList.remove('has-error'));
    });
  }

  function validateContact(container) {
    const name = $('#c-naam', container);
    const email = $('#c-email', container);
    let valid = true;
    if (!name.value.trim()) {
      name.closest('.afc-field').classList.add('has-error');
      valid = false;
    }
    if (!email.value.trim() || !email.value.includes('@')) {
      email.closest('.afc-field').classList.add('has-error');
      valid = false;
    }
    return valid;
  }

  function saveContact(container) {
    state.contact = {
      name: $('#c-naam', container).value.trim(),
      email: $('#c-email', container).value.trim(),
      phone: $('#c-tel', container).value.trim(),
      postcode: $('#c-pc', container).value.trim(),
      message: $('#c-msg', container).value.trim()
    };
  }

  // ─────────────────────────────────────────────
  // SUMMARY STEP
  // ─────────────────────────────────────────────
  function renderSummaryStep(container, step, active) {
    const stepNum = state.currentStep;
    const total = active.length;
    const rows = buildSummaryRows();

    container.innerHTML = `
      <div class="afc-panel">
        <p class="afc-eyebrow">Stap ${stepNum} van ${total}</p>
        <h2 class="afc-panel__title">${step.title}</h2>
        <p class="afc-panel__desc">${step.desc}</p>
        <div class="afc-summary">
          ${rows.map(r => `
            <div class="afc-summary__row">
              <span class="afc-summary__label">${r.label}</span>
              <span class="afc-summary__value">${r.value}</span>
            </div>
          `).join('')}
        </div>
        <div class="afc-nav">
          <button class="afc-btn afc-btn--ghost afc-btn--back" type="button">← Aanpassen</button>
          <button class="afc-btn afc-btn--primary afc-btn--submit" type="button">Aanvraag versturen →</button>
        </div>
        <div class="afc-success afc-view--hidden" id="afc-success">
          <div class="afc-success__icon">✓</div>
          <h3 class="afc-success__title">Aanvraag ontvangen!</h3>
          <p class="afc-success__msg">Bedankt voor je interesse in Abfeco. We nemen binnen 1 werkdag contact met je op met een persoonlijke offerte.</p>
        </div>
      </div>
    `;

    $('.afc-btn--back', container).addEventListener('click', () => goToStep(state.currentStep - 1));
    $('.afc-btn--submit', container).addEventListener('click', submitConfiguration);
  }

  function buildSummaryRows() {
    const rows = [];
    const active = getActiveSteps();

    active.forEach(step => {
      if (step.isContactForm || step.isSummary) return;
      const questions = step.questions(state);
      questions.forEach(q => {
        const val = state.selections[q.id];
        if (val) rows.push({ label: q.label, value: val });
      });
    });

    rows.push({ label: 'Naam', value: state.contact.name });
    rows.push({ label: 'E-mail', value: state.contact.email });
    if (state.contact.phone) rows.push({ label: 'Telefoon', value: state.contact.phone });
    if (state.contact.postcode) rows.push({ label: 'Postcode', value: state.contact.postcode });
    if (state.contact.message) rows.push({ label: 'Opmerking', value: state.contact.message });

    return rows;
  }

  function submitConfiguration() {
    const rows = buildSummaryRows();
    const body = ['WASTAFEL CONFIGURATIE AANVRAAG', ''].concat(rows.map(r => `${r.label}: ${r.value}`)).join('\n');

    $('#hidden-name').value = state.contact.name;
    $('#hidden-email').value = state.contact.email;
    $('#hidden-phone').value = state.contact.phone;
    $('#hidden-postcode').value = state.contact.postcode;
    $('#hidden-body').value = body;

    const form = $('#configurator-contact-form');
    if (form.requestSubmit) {
      form.requestSubmit();
    } else {
      $('#hidden-submit-btn').click();
    }

    const success = $('#afc-success');
    const summary = $('.afc-summary');
    const nav = $('.afc-nav');
    if (success) success.classList.remove('afc-view--hidden');
    if (summary) summary.style.display = 'none';
    if (nav) nav.style.display = 'none';
  }

  // ─────────────────────────────────────────────
  // VALIDATION
  // ─────────────────────────────────────────────
  function validateCurrentStep() {
    const active = getActiveSteps();
    const step = active[state.currentStep - 1];
    if (!step) return true;
    if (step.isContactForm || step.isSummary) return true;

    const questions = step.questions(state);
    return questions.filter(q => q.required).every(q => state.selections[q.id]);
  }

  // ─────────────────────────────────────────────
  // SIDEBAR
  // ─────────────────────────────────────────────
  function updateSidebar() {
    const emptyEl = $('#sidebar-empty');
    const rowsEl = $('#sidebar-rows');
    if (!rowsEl) return;

    const active = getActiveSteps();
    const rows = [];

    active.forEach(step => {
      if (step.isContactForm || step.isSummary) return;
      const questions = step.questions(state);
      questions.forEach(q => {
        const val = state.selections[q.id];
        if (val) rows.push({ label: q.label, value: val });
      });
    });

    if (rows.length === 0) {
      if (emptyEl) emptyEl.style.display = '';
      rowsEl.innerHTML = '';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    rowsEl.innerHTML = rows.map(r => `
      <div class="afc-sidebar__row">
        <span class="afc-sidebar__row-label">${r.label}</span>
        <span class="afc-sidebar__row-value">${r.value}</span>
      </div>
    `).join('');
  }

  // ─────────────────────────────────────────────
  // LIGHTBOX
  // ─────────────────────────────────────────────
  function initGallery() {
    const items = $$('.afc-gallery__item');
    items.forEach((item, idx) => {
      item.addEventListener('click', () => openLightbox(idx));
    });

    const close = $('#lightbox-close');
    const prev = $('#lightbox-prev');
    const next = $('#lightbox-next');
    if (close) close.addEventListener('click', closeLightbox);
    if (prev) prev.addEventListener('click', () => navLightbox(-1));
    if (next) next.addEventListener('click', () => navLightbox(1));

    document.addEventListener('keydown', (e) => {
      if (!$('#afc-lightbox').classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navLightbox(-1);
      if (e.key === 'ArrowRight') navLightbox(1);
    });
  }

  function openLightbox(idx) {
    state.lightboxIndex = idx;
    renderLightbox();
    $('#afc-lightbox').classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    $('#afc-lightbox').classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function navLightbox(dir) {
    const total = $$('.afc-gallery__item').length;
    state.lightboxIndex = (state.lightboxIndex + dir + total) % total;
    renderLightbox();
  }

  function renderLightbox() {
    const content = $('#lightbox-content');
    if (!content) return;
    const galleryImg = $$('.afc-gallery__item img')[state.lightboxIndex];
    const bigSrc = (window.AbfecoConfigurator && window.AbfecoConfigurator.galleryImages)
      ? window.AbfecoConfigurator.galleryImages[state.lightboxIndex]
      : null;

    if (bigSrc) {
      content.innerHTML = `<img src="${bigSrc}" alt="Inspiratie ${state.lightboxIndex + 1}">`;
    } else if (galleryImg) {
      content.innerHTML = `<img src="${galleryImg.src}" alt="${galleryImg.alt || ''}">`;
    }
  }

  // ─────────────────────────────────────────────
  // INIT
  // ─────────────────────────────────────────────
  function init() {
    if (!$('#abfeco-configurator')) return;

    const startBtn = $('#start-configurator');
    if (startBtn) startBtn.addEventListener('click', () => showView('configurator'));

    const backBtn = $('#back-to-gallery');
    if (backBtn) backBtn.addEventListener('click', () => showView('gallery'));

    initGallery();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();